/** Shared constants. Source: docs/data-dictionary.md. */

export const SCHEMA_VERSION = 1;

/** Namespace of all deterministic UUIDv5 identifiers (data dictionary §4). */
export const WIZTIME_NS = '6f1c2a8e-3d4b-5c6d-8e7f-0a1b2c3d4e5f';

export const MINUTES_PER_DAY = 1440;
export const MS_PER_MINUTE = 60_000;
export const MS_PER_HOUR = 3_600_000;
export const HOURS_PER_DAY = 24;

/**
 * How far ahead of the local wall clock a record's hlc may be (clock skew between devices).
 * Records further ahead are rejected on merge and never advance the local clock, so one device
 * with a wrong clock cannot make every later edit of every device sort after the real time.
 */
export const MAX_HLC_DRIFT_MS = 24 * MS_PER_HOUR;

export const EXPORT_FORMAT = 'wiztime-export';
export const EXPORT_VERSION = 1;

/** Field limits (data dictionary §3). */
export const LIMITS = {
  projectNameMax: 40,
  colorMax: 32,
  checkinNameMax: 30,
  blockNoteMax: 200,
  /** Block title: 1-40 characters after trimming (2026-10b §5.1). */
  blockTitleMax: 40,
  dailyNoteMax: 5000,
  tzMax: 64,
  settingKeyMax: 64,
  /** Every-N-days recurrence: N is an integer in 2–365 (data dictionary §3, checkinItem.intervalDays). */
  checkinIntervalMin: 2,
  checkinIntervalMax: 365,
  /** Important date title as written: 1-60 characters after trimming (data dictionary §3). */
  importantDateTitleMax: 60,
  /** Important date note as written: 0-200 characters. */
  importantDateNoteMax: 200,
  /** Record-boundary caps of an important date: generous, so a newer client's looser limits still pass. */
  importantDateTitleBoundaryMax: 200,
  importantDateNoteBoundaryMax: 1000,
  importantDateLeadDaysBoundaryMax: 64,
  /** Bounded string fields that carry an id (calendar id, repeat kind) at the record boundary. */
  importantDateIdBoundaryMax: 32,
  /** Entries of the setting `holidayPacks`. */
  holidayPacksMax: 32,
  holidayPackIdMax: 64,
} as const;

/** Latest day start is 23:00, so a block may end at most 1439 + 1440 minutes after midnight. */
export const MAX_WALL_MIN = 2879;

export const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const HLC_PATTERN = /^[0-9a-f]{12}-[0-9a-f]{4}-[0-9a-f]{8}$/;
export const DEVICE_ID_PATTERN = /^[0-9a-f]{8}$/;

/**
 * Fade rule: how far back (in days) the search for a check-in item's earlier occurrence days may
 * go; fewer than 3 occurrence days inside the window means the item does not fade (data
 * dictionary §9).
 */
export const FADE_LOOKBACK_MAX_DAYS = 60;

/** Most recent revision entries kept on one check-in item or project record (data dictionary §3.1, §9). */
export const MAX_REVISIONS = 100;

/**
 * Upper bound of the JSON length of one record's revision history (data dictionary §3.1): the
 * oldest entries are dropped beyond it, so a record stays well under the sync ciphertext limit.
 */
export const MAX_REVISIONS_JSON_CHARS = 16_000;

/** Data-format version of this build (data dictionary §10): the meaning of stored data, not its tables. */
export const DATA_FORMAT_VERSION = 2;

/** Upper bound of the JSON length of one operation-log entry's changes (data dictionary §11). */
export const OPLOG_ENTRY_MAX_CHARS = 8_000;

/** A timed occurrence can be recorded as done up to this long before its planned time (data dictionary §3). */
export const CHECKIN_EARLY_DONE_MS = 12 * 60 * 60 * 1000;

/** Ticking an occurrence planned more than this ahead of now, or one of a later personal day, asks first. */
export const CHECKIN_FUTURE_CONFIRM_MS = 12 * 60 * 60 * 1000;

/** Lead days (days before an important date at which it is announced) offered as presets; any integer may be entered too (data dictionary §9). */
export const LEAD_DAY_PRESETS: readonly number[] = [0, 1, 2, 3, 7];

/** Lead days of a newly created important date: 3 days before and the day itself. Default of the setting `defaultLeadDays`. */
export const DEFAULT_LEAD_DAYS: readonly number[] = [3, 0];

/** Upper bound of one lead-day value (data dictionary §9). */
export const LEAD_DAYS_MAX = 365;

/** Most entries in one lead-days list and in the setting `defaultLeadDays` (data dictionary §9). */
export const LEAD_DAYS_LIST_MAX = 16;

/** Default of the setting `holidayWindowDays`: holidays within this many days show in the upcoming card (data dictionary §9). */
export const HOLIDAY_WINDOW_DEFAULT_DAYS = 7;

/** Supported year range of every calendar implementation (lunar: lunar year); data dictionary §9. */
export const CALENDAR_YEAR_MIN = 1800;
export const CALENDAR_YEAR_MAX = 2200;
